import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { useBookingPricing } from '../../../../hooks/booking/useBookingPricing'
import { CheckoutMode, useStartCheckout } from '../../../../hooks/booking/useStartCheckout'

import Address from '../../../../components/address/Address'
import { Button } from '../../../../components/buttons/Button'
import FormSectionActions from '../../../../components/formSection/formSectionActions/FormSectionActions'
import Icon from '../../../../components/ui/Icon'
import PageHeader from '../../../../components/ui/pageHeader/PageHeader'
import SummaryPanel from '../../../../components/authenticated/booking/summaryPanel/SummaryPanel'
import WizardLayout from '../../../../components/wizardLayout/WizardLayout'
import GuestUserDetailsStep, {
  type GuestUserDetails,
} from './steps/GuestUserDetailsStep'
import GuestPaymentStep from './steps/GuestPaymentStep'

import {
  AccountCreationRequest,
  AddressModel,
  BookingRecipientType,
  GuestBookingRecipient,
  GuestUserRecipient,
} from '../../../../types/court/court'
import { BookingLocationState } from '../../../../types/booking/booking'
import { formatMoney } from '../../../../utils/booking/booking'
import { FormField } from '../../../../types/formData'
import { hasCompleteBillingAddress } from '../../../../utils/address/address'

const DETAILS_STEP = 1
const ADDRESS_STEP = 2
const PAYMENT_STEP = 3

const EMPTY_ADDRESS: AddressModel = {
  street: '',
  number: '',
  city: '',
  county: '',
  postalCode: '',
}

const GuestCheckout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['bookings', 'address'])
  const { startCheckout } = useStartCheckout()
  const bookingState = location.state as BookingLocationState | null
  const storedDraft = bookingState?.bookingDraft
  const sessions = storedDraft?.sessions ?? []
  // Guests have no passes: prices come from availability (the server prices the checkout again).
  const pricing = useBookingPricing({ sessions, clubId: storedDraft?.clubId, passOwner: null })

  const guestDraft =
    storedDraft?.recipient?.type === BookingRecipientType.GUEST_USER
      ? storedDraft.recipient
      : undefined

  const [step, setStep] = useState(guestDraft ? ADDRESS_STEP : DETAILS_STEP)

  const [details, setDetails] = useState<GuestUserDetails>({
    firstName: {
      value: guestDraft?.firstName ?? '',
      isValid: Boolean(guestDraft?.firstName),
    },
    lastName: {
      value: guestDraft?.lastName ?? '',
      isValid: Boolean(guestDraft?.lastName),
    },
    email: {
      value: guestDraft?.email ?? '',
      isValid: Boolean(guestDraft?.email),
    },
  })

  const [address, setAddress] = useState<AddressModel>(
    guestDraft?.address ?? EMPTY_ADDRESS
  )

  const [addressValid, setAddressValid] = useState(() =>
    hasCompleteBillingAddress(guestDraft?.address ?? EMPTY_ADDRESS)
  )
  const [password, setPassword] = useState<FormField<string>>({
    value: '',
    isValid: false,
  })
  const [termsConsent, setTermsConsent] = useState<FormField<boolean>>({
    value: false,
    isValid: false,
  })
  const [includeAccount, setIncludeAccount] = useState(false)
  const [paymentReady, setPaymentReady] = useState(false)

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior })
  }, [step])

  if (!sessions.length) {
    return <Navigate to="/guest-booking" replace />
  }

  const detailsValid = Object.values(details).every(
    field => field.isValid && Boolean(field.value.trim())
  )

  const accountValid =
    password.isValid && termsConsent.isValid && termsConsent.value
  const detailsStepValid = detailsValid && (!includeAccount || accountValid)
  const addressStepValid = detailsStepValid && addressValid
  const paymentStepValid = addressStepValid && paymentReady
  const stepValidity: Record<number, boolean> = {
    [DETAILS_STEP]: detailsStepValid,
    [ADDRESS_STEP]: addressStepValid,
    [PAYMENT_STEP]: paymentStepValid,
  }
  const canContinue = stepValidity[step]
  const isStepDisabled = (targetStep: number) =>
    (targetStep === ADDRESS_STEP && !stepValidity[DETAILS_STEP]) ||
    (targetStep === PAYMENT_STEP && !stepValidity[PAYMENT_STEP])

  const handleDetailsChange = (
    name: string,
    value: string,
    isValid: boolean
  ) => {
    if (!(name in details)) {
      return
    }

    setDetails(previous => ({
      ...previous,
      [name]: {
        value,
        isValid,
      },
    }))
  }

  const handleAccountCreationChange = (_: string, value: boolean) => {
    setIncludeAccount(value)
    setPaymentReady(false)

    if (!value) {
      setPassword({ value: '', isValid: false })
      setTermsConsent({ value: false, isValid: false })
    }
  }

  const handleStepChange = (nextStep: number) => {
    if (isStepDisabled(nextStep)) {
      return
    }

    if (step === PAYMENT_STEP && nextStep !== PAYMENT_STEP) {
      setPaymentReady(false)
    }

    setStep(nextStep)
  }

  const handleBack = () => {
    if (step === PAYMENT_STEP) {
      setPaymentReady(false)
      setStep(ADDRESS_STEP)
      return
    }

    if (step === ADDRESS_STEP) {
      setStep(DETAILS_STEP)
      return
    }

    navigate('/guest-booking', {
      state: {
        bookingDraft: storedDraft,
      },
    })
  }

  const handleContinue = () => {
    if (!canContinue) {
      return
    }

    if (step === DETAILS_STEP) {
      setStep(ADDRESS_STEP)
      return
    }
  }

  const handleContinueToPayment = () => {
    if (!stepValidity[ADDRESS_STEP]) {
      return
    }

    setPaymentReady(true)
    setStep(PAYMENT_STEP)
  }

  const handlePayment = () => {
    if (!canContinue || step !== PAYMENT_STEP) {
      return
    }

    const guest: GuestUserRecipient = {
      type: BookingRecipientType.GUEST_USER,
      firstName: details.firstName.value.trim(),
      lastName: details.lastName.value.trim(),
      email: details.email.value.trim(),
      address,
    }

    const accountCreation: AccountCreationRequest | undefined = includeAccount
      ? {
          password: password.value,
          termsConsent: termsConsent.value,
        }
      : undefined

    const recipient: GuestBookingRecipient = {
      ...guest,
      ...(accountCreation ? { accountCreation } : {}),
    }

    startCheckout({
      mode: CheckoutMode.GUEST,
      sessions,
      lines: pricing.lines,
      total: pricing.total,
      recipient,
      returnTo: '/guest-booking/checkout',
      returnState: {
        bookingDraft: { ...storedDraft, recipient: guest },
      } satisfies BookingLocationState,
      extraCards: [
        {
          id: 'guest-contact',
          title: t('booking.guest-checkout.details.title'),
          details: [
            { label: t('booking.guest-confirm.full-name'), value: `${guest.firstName} ${guest.lastName}` },
            { label: t('booking.guest-checkout.details.email'), value: guest.email },
          ],
        },
        {
          id: 'guest-address',
          title: t('booking.guest-checkout.address.title'),
          details: [
            { label: t('address:street'), value: guest.address.street },
            { label: t('address:number'), value: guest.address.number },
            ...(guest.address.floor ? [{ label: t('address:floor'), value: guest.address.floor }] : []),
            ...(guest.address.apartment
              ? [{ label: t('address:apartment'), value: guest.address.apartment }]
              : []),
            { label: t('address:city'), value: guest.address.city },
            { label: t('address:county'), value: guest.address.county },
            { label: t('address:postal-code'), value: guest.address.postalCode },
          ],
        },
      ],
    })
  }

  return (
    <div className="guest-checkout">
      <PageHeader
        title={{
          eyebrow: t('booking.guest-checkout.page-header.eyebrow'),
          h1: t('booking.guest-checkout.page-header.title'),
        }}
        sub={t('booking.guest-checkout.page-header.subtitle')}
      />

      <div className="row g-4 align-items-start">
        <WizardLayout
          cardClassName="col-12 col-xl-8"
          steps={[
            t('booking.guest-checkout.steps.details'),
            t('booking.guest-checkout.steps.address'),
            t('booking.guest-checkout.steps.payment'),
          ]}
          currentStep={step}
          ariaLabel={t('booking.guest-checkout.steps.aria-label')}
          onStepChange={handleStepChange}
          isStepDisabled={isStepDisabled}
        >
          {step === DETAILS_STEP ? (
            <GuestUserDetailsStep
              details={details}
              includeAccount={includeAccount}
              password={password}
              termsConsent={termsConsent}
              onDetailsChange={handleDetailsChange}
              onAccountCreationChange={handleAccountCreationChange}
              onPasswordChange={(value, isValid) =>
                setPassword({ value, isValid })
              }
              onTermsChange={(value, isValid) =>
                setTermsConsent({ value, isValid })
              }
            />
          ) : step === ADDRESS_STEP ? (
            <Address
              address={address}
              title={t('booking.guest-checkout.address.title')}
              subtitle={t('booking.guest-checkout.address.subtitle')}
              requiredOnly
              showActions={false}
              onChange={setAddress}
              onValidityChange={setAddressValid}
            />
          ) : (
            <GuestPaymentStep
              email={details.email.value}
              address={address}
              includeAccount={includeAccount}
            />
          )}

          <div className="account-section-actions">
            {step === PAYMENT_STEP ? (
              <Button
                label={t('booking.guest-checkout.steps.back-to-address')}
                variant="ghost"
                size="sm"
                block
                onClick={handleBack}
              />
            ) : step === ADDRESS_STEP ? (
              <FormSectionActions
                isDirty
                isValid={canContinue}
                onSave={handleContinueToPayment}
                onCancel={handleBack}
                saveLabel={t(
                  'booking.guest-checkout.steps.continue-to-payment'
                )}
                resetLabel={t(
                  'booking.guest-checkout.steps.back-to-details'
                )}
              />
            ) : (
              <FormSectionActions
                isDirty
                isValid={canContinue}
                onSave={handleContinue}
                onCancel={handleBack}
                saveLabel={t(
                  'booking.guest-checkout.steps.continue-to-address'
                )}
                resetLabel={t('booking.guest-checkout.back')}
              />
            )}
          </div>
        </WizardLayout>

        <div className="col-12 col-xl-4">
          <SummaryPanel
            lines={pricing.lines}
            total={pricing.total}
            showPrepaid={false}
            showConfirm={step === PAYMENT_STEP}
            confirmDisabled={!canContinue}
            confirmLabel={t('booking.booking-courts.summary-panel.pay', {
              total: formatMoney(pricing.total),
            })}
            onClear={() => navigate('/guest-booking')}
            onConfirm={handlePayment}
          />
        </div>
      </div>

      <section
        className="guest-checkout-next"
        aria-labelledby="guest-checkout-next-title"
      >
        <h3 id="guest-checkout-next-title">
          {t('booking.guest-checkout.next.title')}
        </h3>

        <div className="guest-checkout-next-steps">
          {[
            { icon: 'card', key: 'payment' },
            { icon: 'mail', key: 'confirmation' },
            { icon: 'pin', key: 'arrival' },
          ].map(({ icon, key }, index) => (
            <div className="guest-checkout-next-step" key={key}>
              <div className="guest-checkout-next-icon" aria-hidden="true">
                <Icon name={icon} size={20} strokeWidth={2} />
              </div>
              <div>
                <div className="guest-checkout-next-label mono">
                  {index + 1}. {t(`booking.guest-checkout.next.${key}.title`)}
                </div>
                <p>{t(`booking.guest-checkout.next.${key}.description`)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

export default GuestCheckout
